import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Loader2, RefreshCw, Send, CheckCircle, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_CHOICES = [0, 1, 2, 3, 7];

export const TrainingAttendanceTemplate = ({ settings, updateField, onSaved }) => {
  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [schedule, setSchedule] = useState(null);

  const cfg = settings.training_reminder || {};
  const days = cfg.days_before || [];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        axios.get(`${API}/admin/whatsapp/training-template`, { withCredentials: true }),
        axios.get(`${API}/admin/whatsapp/training-reminders/schedule`, { withCredentials: true }),
      ]);
      setTpl(t.data);
      setDraft(t.data.body || "");
      setSchedule(s.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the training template");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const register = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/whatsapp/training-template`,
        { body: draft, force: tpl?.registered && tpl?.status === "rejected" },
        { withCredentials: true }
      );
      toast.success(r.data.already ? "Already registered with WhatsApp" : "Submitted to WhatsApp for approval");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/whatsapp/training-reminders/run`, { ignore_window: true },
        { withCredentials: true });
      toast.success(r.data.skipped
        ? `Nothing sent (${r.data.reason})`
        : `Sent ${r.data.total_sent}, skipped ${r.data.not_sent}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleDay = (d) => {
    const next = days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort((a, b) => a - b);
    updateField("training_reminder.days_before", next);
  };

  const status = tpl?.status || "";
  const statusTone = status === "approved"
    ? "bg-emerald-500/20 text-emerald-300"
    : status === "rejected"
      ? "bg-red-500/20 text-red-300"
      : "bg-amber-500/20 text-amber-300";

  return (
    <div className="mt-4 pt-4 border-t border-slate-800 space-y-4" data-testid="training-attendance-template">
      <div>
        <Label className="text-white text-sm">When to send</Label>
        <div className="flex gap-2 mt-1 flex-wrap items-center">
          {DAY_CHOICES.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => toggleDay(d)}
              className={`px-3 py-1.5 text-xs rounded border transition-colors ${
                days.includes(d)
                  ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-300"
                  : "border-slate-700 text-slate-300 hover:bg-slate-800"
              }`}
              data-testid={`training-days-${d}`}
            >
              {d === 0 ? "Same day" : d === 1 ? "1 day before" : `${d} days before`}
            </button>
          ))}
          <div className="flex items-center gap-2 ml-2">
            <span className="text-xs text-slate-400">at</span>
            <Input
              type="time"
              value={cfg.send_time || "12:00"}
              onChange={(e) => updateField("training_reminder.send_time", e.target.value)}
              className="w-28 bg-slate-950 border-slate-700 text-white h-8"
              data-testid="training-send-time"
            />
            <span className="text-xs text-slate-500">Singapore time</span>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          e.g. 1 day before at 12:00 sends Thursday lunchtime for Friday training.
          Remember to press Save Settings.
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="text-white text-sm">WhatsApp message (with Yes / No buttons)</Label>
          <div className="flex items-center gap-2">
            {tpl?.registered && (
              <Badge className={`text-[10px] ${statusTone}`} data-testid="training-template-status">
                {status || "pending"}
              </Badge>
            )}
            <Button size="sm" variant="outline" className="border-slate-700 text-slate-300 h-7"
              onClick={load} disabled={loading} data-testid="training-template-refresh">
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            </Button>
          </div>
        </div>
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={6}
          className="bg-slate-950 border-slate-700 text-white font-mono text-xs"
          data-testid="training-template-body"
        />
        <p className="text-xs text-slate-500 mt-1">
          {"{{1}}"} date · {"{{2}}"} pitch · {"{{3}}"} time · {"{{4}}"} child. Parents tap
          <strong className="text-slate-300"> Yes, coming</strong> or
          <strong className="text-slate-300"> No, cannot make it</strong> — no typing.
        </p>
        {tpl?.rejection_reason && (
          <p className="text-xs text-red-300 mt-1">WhatsApp said: {tpl.rejection_reason}</p>
        )}
        <div className="flex gap-2 mt-2 flex-wrap">
          {tpl?.default_body && draft !== tpl.default_body && (
            <Button size="sm" variant="ghost" className="text-slate-400 h-8"
              onClick={() => setDraft(tpl.default_body)}
              data-testid="training-template-reset">
              Reset wording
            </Button>
          )}
          <Button size="sm" onClick={register} disabled={busy || !draft.trim()}
            className="bg-emerald-600 hover:bg-emerald-700" data-testid="training-template-register">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
            {tpl?.registered ? "Re-submit wording" : "Register with WhatsApp"}
          </Button>
          <Button size="sm" variant="outline" className="border-slate-700 text-slate-300"
            onClick={runNow} disabled={busy} data-testid="training-reminders-run">
            <Send className="w-3.5 h-3.5 mr-1" /> Send due reminders now
          </Button>
        </div>
      </div>

      {schedule?.sessions?.length > 0 && (
        <div data-testid="training-reminder-schedule">
          <Label className="text-white text-sm flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> Next reminders
          </Label>
          <div className="mt-1 space-y-1 max-h-48 overflow-y-auto">
            {schedule.sessions.slice(0, 12).map((s) => (
              <div key={s.session_id} className="text-xs text-slate-400 flex gap-2 flex-wrap">
                <span className="text-slate-200">{s.date}</span>
                <span>{s.team_name}</span>
                {s.sends.map((x) => (
                  <span key={x.days_before} className={x.past ? "text-slate-600" : "text-emerald-400/80"}>
                    → {new Date(x.send_at).toLocaleString("en-SG", { dateStyle: "medium", timeStyle: "short" })}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
